/**
 * API error helpers for the applet bridge and consumers (e.g. TanStack Query).
 */
const MAX_JSON_SUMMARY_LEN = 500;
/**
 * Produces a short human-readable summary from an API error body (JSON or text).
 * Used by the shell when filling the optional `error` field on {@link VenueOSApiResponsePayload},
 * and as the `Error.message` for {@link VenueOSApiHttpError}.
 */
export function extractApiErrorSummary(body) {
    if (body === null || body === undefined) {
        return 'Request failed';
    }
    if (typeof body === 'string') {
        return body.length > MAX_JSON_SUMMARY_LEN ? body.slice(0, MAX_JSON_SUMMARY_LEN) + '…' : body;
    }
    if (typeof body === 'object' && body !== null) {
        const message = Reflect.get(body, 'message');
        if (typeof message === 'string' && message.length > 0) {
            return message;
        }
        const errorCode = Reflect.get(body, 'error');
        if (typeof errorCode === 'string' && errorCode.length > 0) {
            return errorCode;
        }
        try {
            const serialized = JSON.stringify(body);
            if (serialized.length <= MAX_JSON_SUMMARY_LEN) {
                return serialized;
            }
        }
        catch {
            /* ignore */
        }
    }
    return 'Request failed';
}
/**
 * Maps a shell {@link VenueOSApiResponsePayload} to the client `fetch` result shape.
 * On HTTP failure, preserves the parsed response body; if the body was omitted, uses `{ error }`.
 */
export function buildApiFetchResponseFromPayload(payload) {
    const { success, status, data: responseData, error } = payload;
    if (success) {
        return {
            ok: true,
            status,
            data: responseData,
        };
    }
    const failureData = responseData !== undefined ? responseData : { error: error ?? 'Request failed' };
    return {
        ok: false,
        status,
        data: failureData,
    };
}
/**
 * Thrown by {@link throwIfApiError} when `response.ok` is false. Carries HTTP status and parsed body.
 */
export class VenueOSApiHttpError extends Error {
    constructor(status, data) {
        super(extractApiErrorSummary(data));
        this.name = 'VenueOSApiHttpError';
        this.status = status;
        this.data = data;
    }
}
/**
 * Narrows a bridge response after an HTTP error would have been thrown by openapi-fetch-style clients.
 *
 * @example
 * ```ts
 * const res = await venueos.api.get('/api/foo', { params: { path: { id } } });
 * throwIfApiError(res);
 * return res.data;
 * ```
 */
export function throwIfApiError(response) {
    if (!response.ok) {
        throw new VenueOSApiHttpError(response.status, response.data);
    }
}
//# sourceMappingURL=api-error.js.map